import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material';
import React, {useCallback, useMemo, useState} from 'react';
import ProfileIdentity from './ProfileIdentity';
import {getMean, getStandardDeviation} from '../../services/mathService';
import {positions} from '../../services/teamService';
import {compare} from '../../helpers';
import TeamShiftUtilizationRow from './TeamShiftUtilizationRow';
import {PlayerSessionShift, TeamSession} from '../../interfaces';
import {useWindowSize} from '../../hooks/useWindowSize';
import {useTranslation} from 'react-i18next';
import {getEnergyRatioDifference} from '../../services/measurementService';
import HeliosToggleButton from './HeliosToggleButton';
import TeamShiftUtilizationSort, {
  MetricSortObject,
} from './TeamShiftUtilizationSort';
import TeamShiftUtilizationFilter, {
  UtilizationFilterForm,
} from './TeamShiftUtilizationFilter';
import {Positions, SessionTagCodes} from '../../enums';
import {createSessionTagFilter} from '../../services/sessionTagService';

interface PlayerPeriodData {
  playerId: number;
  number: number;
  firstName: string;
  lastName: string;
  position: string;
  profilePictureFileKey: string;
  errorState?: 'pendingUpload' | 'noData';
  timeOnIce: number;
  shiftCount: number;
  energyRatio: number;
  baselineEnergyRatio: number | null;
}

export enum Metric {
  timeOnIce = 'timeOnIce',
  energyRatio = 'energyRatio',
  shiftCount = 'shiftCount',
}

type UtilizationData = Record<Metric, number>;

export interface PlayerData {
  playerId: number;
  number?: number;
  firstName: string;
  lastName: string;
  position: string;
  profilePictureFileKey: string;
  fullGameErrorState?: 'pendingUpload' | 'noData';
  fullGameTimeOnIce: number;
  fullGameShiftCount: number;
  fullGameEnergyRatio: number;
  fullGameEnergyRatioBaselineDiff: string;
  fullGameUtilization: UtilizationData;
  fullGameBaselineEnergyRatio: number;
  period1ErrorState?: 'pendingUpload' | 'noData';
  period1TimeOnIce: number;
  period1ShiftCount: number;
  period1EnergyRatio: number;
  period1EnergyRatioBaselineDiff: string;
  period1Utilization: UtilizationData;
  period1BaselineEnergyRatio: number;
  period2ErrorState?: 'pendingUpload' | 'noData';
  period2TimeOnIce: number;
  period2ShiftCount: number;
  period2EnergyRatio: number;
  period2EnergyRatioBaselineDiff: string;
  period2Utilization: UtilizationData;
  period2BaselineEnergyRatio: number;
  period3ErrorState?: 'pendingUpload' | 'noData';
  period3TimeOnIce: number;
  period3ShiftCount: number;
  period3EnergyRatio: number;
  period3EnergyRatioBaselineDiff: string;
  period3Utilization: UtilizationData;
  period3BaselineEnergyRatio: number;
  overtimeErrorState?: 'pendingUpload' | 'noData';
  overtimeTimeOnIce: number;
  overtimeShiftCount: number;
  overtimeEnergyRatio: number;
  overtimeEnergyRatioBaselineDiff: string;
  overtimeUtilization: UtilizationData;
  overtimeBaselineEnergyRatio: number;
}

const getMultiSessionPeriodBaselineData = (
  periodTeamSessions: TeamSession[],
  playerId: number,
) => {
  const playerSessions = periodTeamSessions
    .flatMap(ts => ts.playerSessions || [])
    .filter(ps => ps.playerId === playerId && ps.valid);
  const sessionsWithBaselines = playerSessions.filter(ps => !!ps.baseline);
  if (!sessionsWithBaselines.length) {
    return null;
  } else if (sessionsWithBaselines.length > 1) {
    sessionsWithBaselines.sort((a, b) => {
      const durationA =
        new Date(a.endTime || 0).valueOf() -
        new Date(a.startTime || 0).valueOf();
      const durationB =
        new Date(b.endTime || 0).valueOf() -
        new Date(b.startTime || 0).valueOf();
      return durationB - durationA;
    });
  }
  return sessionsWithBaselines[0].baseline;
};

const getMultiSessionPeriodEnergyRatio = (
  periodTeamSessions: TeamSession[],
  playerId: number,
) => {
  const sessions = periodTeamSessions
    .flatMap(ts => ts.playerSessions || [])
    .filter(ps => ps.playerId === playerId && ps.valid);

  // Time-weighted average of each session's cloud energyRatio, weighted by the
  // session's time on ice (valid shift durations) so longer sessions count more.
  let weightedSum = 0;
  let totalTimeOnIce = 0;
  for (const session of sessions) {
    if (session.energyRatio == null) {
      continue;
    }
    const timeOnIce = (session.playerSessionShifts ?? [])
      .filter((shift: PlayerSessionShift) => shift.valid)
      .reduce(
        (acc: number, shift: PlayerSessionShift) =>
          acc + ((shift.end || 0) - (shift.start || 0)),
        0,
      );
    weightedSum += session.energyRatio * timeOnIce;
    totalTimeOnIce += timeOnIce;
  }

  return totalTimeOnIce > 0 ? weightedSum / totalTimeOnIce : null;
};

const getPeriodData = ({
  periodTeamSessions,
}: {
  periodTeamSessions: TeamSession[];
}) => {
  const periodData: PlayerPeriodData[] = [];
  for (let i = 0; i < periodTeamSessions.length; i++) {
    const teamSession = periodTeamSessions[i];
    if (!teamSession.playerSessions) {
      continue;
    }
    for (let j = 0; j < teamSession.playerSessions.length; j++) {
      const playerSession = teamSession.playerSessions[j];
      const playerSessionShifts = (
        teamSession.playerSessions[j]?.playerSessionShifts ?? []
      ).filter((shift: PlayerSessionShift) => shift.valid);
      const currentPlayer = periodData.filter(
        x => x.playerId === playerSession?.player?.id,
      );
      if (currentPlayer.length === 1) {
        currentPlayer[0].timeOnIce += playerSession?.timeOnIce || 0;
        currentPlayer[0].shiftCount += playerSessionShifts.length;
      } else {
        periodData.push({
          playerId: playerSession?.player?.id || 0,
          number: playerSession?.player?.number || 0,
          firstName: playerSession?.player?.profile?.firstName || '',
          lastName: playerSession?.player?.profile?.lastName || '',
          position: playerSession?.player?.profile?.position || '',
          profilePictureFileKey:
            playerSession?.player?.profile?.profilePictureFileKey || '',
          errorState: playerSession
            ? playerSession.hasPlayerData
              ? undefined
              : 'pendingUpload'
            : 'noData',
          timeOnIce: playerSession?.timeOnIce || 0,
          shiftCount: playerSessionShifts.length,
          energyRatio:
            getMultiSessionPeriodEnergyRatio(
              periodTeamSessions,
              playerSession.player?.id ?? 0,
            ) ?? 0,
          baselineEnergyRatio:
            getMultiSessionPeriodBaselineData(
              periodTeamSessions,
              playerSession.player?.id ?? 0,
            )?.energyRatio ?? null,
        });
      }
    }
  }
  return periodData;
};

const getUtilization = (
  periodData: PlayerPeriodData[],
  playerData: PlayerPeriodData,
  metric: Metric,
) => {
  const non0Data = periodData
    .map(player => {
      if (metric === 'timeOnIce') {
        return player.timeOnIce;
      } else if (metric === 'shiftCount') {
        return player.shiftCount;
      } else if (metric === 'energyRatio') {
        return player.energyRatio;
      }
      return 0;
    })
    .filter(metric => metric > 0);

  if (non0Data.length > 0) {
    const non0Mean = getMean(non0Data);
    const non0StdDev = getStandardDeviation(non0Data);
    if (non0Mean !== null && non0StdDev !== null) {
      const non02StdDevMin = non0Mean - 2 * non0StdDev;
      const non02StdDevMax = non0Mean + 2 * non0StdDev;
      return non0Mean > 0 && playerData?.timeOnIce > 0
        ? ((playerData?.[metric] ?? 0) - non02StdDevMin) /
            (non02StdDevMax - non02StdDevMin)
        : 0;
    }
  }

  return 0;
};

const UtilizationRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
}));

const UtilizationCell = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 10,
}));

interface Props {
  teamSessions: TeamSession[];
}

const TeamShiftUtilization: React.FC<Props> = ({teamSessions}) => {
  const {t} = useTranslation();
  const [metric, setMetric] = useState<
    'timeOnIce' | 'shiftCount' | 'energyRatio' | 'fullView'
  >('fullView');
  const [sort, setSort] = useState<MetricSortObject>();
  const [filter, setFilter] = useState<UtilizationFilterForm>();
  let fullGameTeamSessions = teamSessions.filter(
    createSessionTagFilter([SessionTagCodes.fullGame]),
  );
  const period1TeamSessions = teamSessions.filter(
    createSessionTagFilter([SessionTagCodes.period1]),
  );
  const period2TeamSessions = teamSessions.filter(
    createSessionTagFilter([SessionTagCodes.period2]),
  );
  const period3TeamSessions = teamSessions.filter(
    createSessionTagFilter([SessionTagCodes.period3]),
  );
  const overtimeTeamSessions = teamSessions.filter(
    createSessionTagFilter([SessionTagCodes.overtime]),
  );

  const [viewMode, setViewMode] = useState<'totals' | 'byPeriod' | null>(
    teamSessions.length <= 1 ? null : 'totals',
  );

  if (
    fullGameTeamSessions.length === 0 &&
    period1TeamSessions.length === 0 &&
    period2TeamSessions.length === 0 &&
    period3TeamSessions.length === 0 &&
    overtimeTeamSessions.length === 0 &&
    teamSessions.length === 1 &&
    teamSessions[0].type === 'game'
  ) {
    fullGameTeamSessions = teamSessions;
  }

  const fullGameData = fullGameTeamSessions.length
    ? getPeriodData({
        periodTeamSessions: [...fullGameTeamSessions],
      })
    : getPeriodData({
        periodTeamSessions: [
          ...period1TeamSessions,
          ...period2TeamSessions,
          ...period3TeamSessions,
          ...overtimeTeamSessions,
        ],
      });
  const period1Data = getPeriodData({
    periodTeamSessions: period1TeamSessions,
  });
  const period2Data = getPeriodData({
    periodTeamSessions: period2TeamSessions,
  });
  const period3Data = getPeriodData({
    periodTeamSessions: period3TeamSessions,
  });
  const overtimeData = getPeriodData({
    periodTeamSessions: overtimeTeamSessions,
  });

  const players = useMemo(
    () => [
      ...fullGameData,
      ...period1Data,
      ...period2Data,
      ...period3Data,
      ...overtimeData,
    ],
    [fullGameData, overtimeData, period1Data, period2Data, period3Data],
  );

  // order players by position
  // ignore goalies from utilization
  const playerIds = players
    .filter(
      player =>
        positions.includes(player.position) && player.position !== 'goalie',
    )
    .sort((a: PlayerPeriodData, b: PlayerPeriodData) => {
      return (
        compare(positions.indexOf(a.position), positions.indexOf(b.position)) ||
        compare(a.lastName, b.lastName)
      );
    })
    .map(player => player.playerId);

  const uniquePlayerIds = useMemo(() => [...new Set(playerIds)], [playerIds]);

  const fullGamePlayerUtilization: PlayerData[] = useMemo(
    () =>
      uniquePlayerIds.map(playerId => {
        const activePlayer = players.filter(
          player => player.playerId === playerId,
        )[0];
        const fullGamePlayer = fullGameData.filter(
          player => player.playerId === playerId,
        )[0];
        const period1Player = period1Data.filter(
          player => player.playerId === playerId,
        )[0];
        const period2Player = period2Data.filter(
          player => player.playerId === playerId,
        )[0];
        const period3Player = period3Data.filter(
          player => player.playerId === playerId,
        )[0];
        const overtimePlayer = overtimeData.filter(
          player => player.playerId === playerId,
        )[0];

        return {
          playerId,
          firstName: activePlayer?.firstName,
          lastName: activePlayer?.lastName,
          position: activePlayer?.position,
          profilePictureFileKey: activePlayer?.profilePictureFileKey,
          fullGameErrorState: fullGamePlayer
            ? fullGamePlayer?.errorState
            : 'noData',
          fullGameTimeOnIce: fullGamePlayer?.timeOnIce ?? 0,
          fullGameShiftCount: fullGamePlayer?.shiftCount ?? 0,
          fullGameEnergyRatio: fullGamePlayer?.energyRatio ?? 0,
          fullGameEnergyRatioBaselineDiff: getEnergyRatioDifference(
            fullGamePlayer?.energyRatio,
            fullGamePlayer?.baselineEnergyRatio,
          ),
          fullGameUtilization: {
            timeOnIce: getUtilization(
              fullGameData,
              fullGamePlayer,
              Metric.timeOnIce,
            ),
            shiftCount: getUtilization(
              fullGameData,
              fullGamePlayer,
              Metric.shiftCount,
            ),
            energyRatio: getUtilization(
              fullGameData,
              fullGamePlayer,
              Metric.energyRatio,
            ),
          },
          fullGameBaselineEnergyRatio: fullGamePlayer?.baselineEnergyRatio ?? 0,
          period1ErrorState: period1Player
            ? period1Player.errorState
            : 'noData',
          period1TimeOnIce: period1Player?.timeOnIce ?? 0,
          period1ShiftCount: period1Player?.shiftCount ?? 0,
          period1EnergyRatio: period1Player?.energyRatio ?? 0,
          period1EnergyRatioBaselineDiff: getEnergyRatioDifference(
            period1Player?.energyRatio,
            period1Player?.baselineEnergyRatio,
          ),
          period1Utilization: {
            timeOnIce: getUtilization(
              period1Data,
              period1Player,
              Metric.timeOnIce,
            ),
            shiftCount: getUtilization(
              period1Data,
              period1Player,
              Metric.shiftCount,
            ),
            energyRatio: getUtilization(
              period1Data,
              period1Player,
              Metric.energyRatio,
            ),
          },
          period1BaselineEnergyRatio: period1Player?.baselineEnergyRatio ?? 0,
          period2ErrorState: period2Player
            ? period2Player?.errorState
            : 'noData',
          period2TimeOnIce: period2Player?.timeOnIce ?? 0,
          period2ShiftCount: period2Player?.shiftCount ?? 0,
          period2EnergyRatio: period2Player?.energyRatio ?? 0,
          period2EnergyRatioBaselineDiff: getEnergyRatioDifference(
            period2Player?.energyRatio,
            period2Player?.baselineEnergyRatio,
          ),
          period2Utilization: {
            timeOnIce: getUtilization(
              period2Data,
              period2Player,
              Metric.timeOnIce,
            ),
            shiftCount: getUtilization(
              period2Data,
              period2Player,
              Metric.shiftCount,
            ),
            energyRatio: getUtilization(
              period2Data,
              period2Player,
              Metric.energyRatio,
            ),
          },
          period2BaselineEnergyRatio: period2Player?.baselineEnergyRatio ?? 0,
          period3ErrorState: period3Player
            ? period3Player?.errorState
            : 'noData',
          period3TimeOnIce: period3Player?.timeOnIce ?? 0,
          period3ShiftCount: period3Player?.shiftCount ?? 0,
          period3EnergyRatio: period3Player?.energyRatio ?? 0,
          period3EnergyRatioBaselineDiff: getEnergyRatioDifference(
            period3Player?.energyRatio,
            period3Player?.baselineEnergyRatio,
          ),
          period3Utilization: {
            timeOnIce: getUtilization(
              period3Data,
              period3Player,
              Metric.timeOnIce,
            ),
            shiftCount: getUtilization(
              period3Data,
              period3Player,
              Metric.shiftCount,
            ),
            energyRatio: getUtilization(
              period3Data,
              period3Player,
              Metric.energyRatio,
            ),
          },
          period3BaselineEnergyRatio: period3Player?.baselineEnergyRatio ?? 0,
          overtimeErrorState: overtimePlayer
            ? overtimePlayer?.errorState
            : 'noData',
          overtimeTimeOnIce: overtimePlayer?.timeOnIce ?? 0,
          overtimeShiftCount: overtimePlayer?.shiftCount ?? 0,
          overtimeEnergyRatio: overtimePlayer?.energyRatio ?? 0,
          overtimeEnergyRatioBaselineDiff: getEnergyRatioDifference(
            overtimePlayer?.energyRatio,
            overtimePlayer?.baselineEnergyRatio,
          ),
          overtimeUtilization: {
            timeOnIce: getUtilization(
              overtimeData,
              overtimePlayer,
              Metric.timeOnIce,
            ),
            shiftCount: getUtilization(
              overtimeData,
              overtimePlayer,
              Metric.shiftCount,
            ),
            energyRatio: getUtilization(
              overtimeData,
              overtimePlayer,
              Metric.energyRatio,
            ),
          },
          overtimeBaselineEnergyRatio: overtimePlayer?.baselineEnergyRatio ?? 0,
        };
      }),
    [
      fullGameData,
      overtimeData,
      period1Data,
      period2Data,
      period3Data,
      players,
      uniquePlayerIds,
    ],
  );

  const [width] = useWindowSize();

  const metricSort = useCallback(
    (a: PlayerData, b: PlayerData) => {
      if (!sort || !sort.direction) {
        return 0;
      }
      let aMetric = 0;
      let bMetric = 0;
      switch (sort.sortedMetric) {
        case 'energyRatio':
          aMetric = a[`${sort?.period || 'fullGame'}EnergyRatio`];
          bMetric = b[`${sort?.period || 'fullGame'}EnergyRatio`];
          break;
        case 'shiftCount':
          aMetric = a[`${sort?.period || 'fullGame'}ShiftCount`];
          bMetric = b[`${sort?.period || 'fullGame'}ShiftCount`];
          break;
        case 'timeOnIce':
          aMetric = a[`${sort?.period || 'fullGame'}TimeOnIce`];
          bMetric = b[`${sort?.period || 'fullGame'}TimeOnIce`];
          break;
        default:
          return 0;
      }
      return sort.direction === 'ASC' ? aMetric - bMetric : bMetric - aMetric;
    },
    [sort],
  );

  const playerFilter = useCallback(
    (playerData: PlayerData) => {
      if (
        !filter ||
        Object.values(filter).every(val => !val) ||
        Object.values(filter).every(val => val)
      ) {
        return true;
      }
      if (filter.defense && playerData.position === Positions.defense) {
        return true;
      }
      if (
        filter.forwards &&
        [Positions.center, Positions.leftwing, Positions.rightwing].indexOf(
          playerData.position as Positions,
        ) !== -1
      ) {
        return true;
      }
      if (filter.center && playerData.position === Positions.center) {
        return true;
      }
      if (filter.leftwing && playerData.position === Positions.leftwing) {
        return true;
      }
      if (filter.rightwing && playerData.position === Positions.rightwing) {
        return true;
      }
      return false;
    },
    [filter],
  );

  if (
    fullGameTeamSessions.length > 0 ||
    period1TeamSessions.length > 0 ||
    period2TeamSessions.length > 0 ||
    period3TeamSessions.length > 0 ||
    overtimeTeamSessions.length > 0
  ) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            justifyContent: 'space-between',
          }}>
          <Box>
            <Button
              sx={{mr: 2, mb: 2}}
              variant={metric === 'fullView' ? 'contained' : 'outlined'}
              onClick={() => setMetric('fullView')}>
              {t('teamShiftUtilization.fullView')}
            </Button>
            <Button
              sx={{mr: 2, mb: 2}}
              variant={metric === Metric.timeOnIce ? 'contained' : 'outlined'}
              onClick={() => setMetric(Metric.timeOnIce)}>
              {t('teamShiftUtilization.timeOnIce')}
            </Button>
            <Button
              sx={{mr: 2, mb: 2}}
              variant={metric === Metric.energyRatio ? 'contained' : 'outlined'}
              onClick={() => setMetric(Metric.energyRatio)}>
              {t('teamShiftUtilization.energyRatio')}
            </Button>
            <Button
              sx={{mr: 2, mb: 2}}
              variant={metric === Metric.shiftCount ? 'contained' : 'outlined'}
              onClick={() => setMetric(Metric.shiftCount)}>
              {t('teamShiftUtilization.shiftCount')}
            </Button>
          </Box>
          <Box sx={{textAlign: 'right'}}>
            {viewMode && (
              <ToggleButtonGroup
                sx={{mb: 2}}
                value={viewMode}
                onChange={(_, v) => {
                  if (v) {
                    setViewMode(v);
                  }
                }}
                exclusive>
                <HeliosToggleButton value={'totals'} key="totals">
                  {t('teamShiftUtilization.totals')}
                </HeliosToggleButton>
                <HeliosToggleButton value={'byPeriod'} key="byPeriod">
                  {t('teamShiftUtilization.byPeriod')}
                </HeliosToggleButton>
              </ToggleButtonGroup>
            )}
            <TeamShiftUtilizationFilter
              currentFilters={filter}
              onFilterUpdate={setFilter}
            />
          </Box>
        </Box>
        <TableContainer sx={{mb: 5}} component={Paper}>
          <Table
            aria-label={t('ariaLabels.tables.teamShiftUtilization') as string}
            padding="none"
            sx={{minWidth: '700px'}}>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{width: '100%', paddingRight: 0}}>
                  <UtilizationRow>
                    {viewMode === 'totals' ? (
                      <UtilizationCell>
                        <Typography variant="h4">
                          {t('teamShiftUtilization.fullGame')}
                          {metric !== 'fullView' && (
                            <TeamShiftUtilizationSort
                              currentSort={sort}
                              sortedMetric={metric}
                              onSortChange={setSort}
                            />
                          )}
                        </Typography>
                      </UtilizationCell>
                    ) : (
                      <>
                        {period1TeamSessions.length > 0 ? (
                          <UtilizationCell>
                            <Typography variant="h4">
                              {t('teamShiftUtilization.period1')}
                              {metric !== 'fullView' && (
                                <TeamShiftUtilizationSort
                                  currentSort={sort}
                                  sortedMetric={metric}
                                  onSortChange={setSort}
                                  period="period1"
                                />
                              )}
                            </Typography>
                          </UtilizationCell>
                        ) : null}
                        {period2TeamSessions.length > 0 ? (
                          <UtilizationCell>
                            <Typography variant="h4">
                              {t('teamShiftUtilization.period2')}
                              {metric !== 'fullView' && (
                                <TeamShiftUtilizationSort
                                  currentSort={sort}
                                  sortedMetric={metric}
                                  onSortChange={setSort}
                                  period="period2"
                                />
                              )}
                            </Typography>
                          </UtilizationCell>
                        ) : null}
                        {period3TeamSessions.length > 0 ? (
                          <UtilizationCell>
                            <Typography variant="h4">
                              {t('teamShiftUtilization.period3')}
                              {metric !== 'fullView' && (
                                <TeamShiftUtilizationSort
                                  currentSort={sort}
                                  sortedMetric={metric}
                                  onSortChange={setSort}
                                  period="period3"
                                />
                              )}
                            </Typography>
                          </UtilizationCell>
                        ) : null}
                        {overtimeTeamSessions.length > 0 ? (
                          <UtilizationCell>
                            <Typography variant="h4">
                              {t('teamShiftUtilization.overtime')}
                              {metric !== 'fullView' && (
                                <TeamShiftUtilizationSort
                                  currentSort={sort}
                                  sortedMetric={metric}
                                  onSortChange={setSort}
                                  period="overtime"
                                />
                              )}
                            </Typography>
                          </UtilizationCell>
                        ) : null}
                      </>
                    )}
                  </UtilizationRow>
                </TableCell>
              </TableRow>
              {(viewMode === 'totals' || viewMode === null) &&
                metric === 'fullView' && (
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell sx={{width: '100%', paddingRight: 0}}>
                      <UtilizationRow>
                        <UtilizationCell>
                          <Typography variant="h4">
                            {t('teamShiftUtilization.timeOnIce')}
                            <TeamShiftUtilizationSort
                              currentSort={sort}
                              sortedMetric={Metric.timeOnIce}
                              onSortChange={setSort}
                            />
                          </Typography>
                        </UtilizationCell>
                        <UtilizationCell>
                          <Typography variant="h4">
                            {t('teamShiftUtilization.energyRatio')}
                            <TeamShiftUtilizationSort
                              currentSort={sort}
                              sortedMetric={Metric.energyRatio}
                              onSortChange={setSort}
                            />
                          </Typography>
                        </UtilizationCell>
                        <UtilizationCell>
                          <Typography variant="h4">
                            {t('teamShiftUtilization.shiftCount')}
                            <TeamShiftUtilizationSort
                              currentSort={sort}
                              sortedMetric={Metric.shiftCount}
                              onSortChange={setSort}
                            />
                          </Typography>
                        </UtilizationCell>
                      </UtilizationRow>
                    </TableCell>
                  </TableRow>
                )}
            </TableHead>
            <TableBody>
              {fullGamePlayerUtilization
                .filter(playerFilter)
                .sort(metricSort)
                .map(player => (
                  <TableRow key={player.playerId} sx={{height: '1px'}}>
                    <TableCell sx={{whiteSpace: 'nowrap', padding: '3px'}}>
                      <ProfileIdentity
                        firstName={width > 1200 ? player.firstName : ''}
                        lastName={player.lastName}
                        position={player.position}
                        profilePictureUrl={player.profilePictureFileKey}
                        // number={player.number}
                        // expandNumberIfEmpty={expandPlayerNumber}
                        size="small"
                        sx={{pl: 1}}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '100%',
                        height: 'inherit',
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingRight: 0,
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                        }}>
                        <TeamShiftUtilizationRow
                          viewMode={viewMode}
                          player={player}
                          selectedMetric={metric}
                          fullGameTeamSessions={fullGameTeamSessions}
                          period1TeamSessions={period1TeamSessions}
                          period2TeamSessions={period2TeamSessions}
                          period3TeamSessions={period3TeamSessions}
                          overtimeTeamSessions={overtimeTeamSessions}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }
  return null;
};

export default TeamShiftUtilization;
